import UIComponent from "../ui/component";

export class BulkExportModal extends UIComponent {
    init() {
        this.includeComponents({
            transcriptionsList: "document@[data-tbx-component=TranscriptionsList]",
        });

        this.states = {
            speakers: false,
            timestamps: false,
            subtitles: false,
            maxLines: 2,
            maxSymbols: 35,
        };
        this.selectedIds = [];
        this.isSrtDisabled = false;

        this.refreshFromInputs();
        this.bindEvents();
        this.updateSelectionState();
    }

    bindEvents() {
        this.bindCheckbox("speakersCheckbox", (checked) => {
            this.states.speakers = checked;
            this.updateHrefs();
        });
        this.bindCheckbox("timestampsCheckbox", (checked) => {
            this.states.timestamps = checked;
            this.updateHrefs();
        });
        this.bindCheckbox("subtitlesCheckbox", (checked) => {
            this.states.subtitles = checked;
            this.toggleSpeakerSelect();
            this.updateHrefs();
        });

        this.selectSpeaker.selectStroke?.on("change", () => {
            this.states.maxLines = this.getSelectValue("selectStroke", 2);
            this.updateHrefs();
        });
        this.selectSpeaker.selectSymbol?.on("change", () => {
            this.states.maxSymbols = this.getSelectValue("selectSymbol", 35);
            this.updateHrefs();
        });

        this.transcriptionsList?.on("selectionChange", () =>
            this.updateSelectionState()
        );

        this.subtitlesExportLink?.on("click", (event) => {
            if (this.isSrtDisabled) {
                event.preventDefault();
                event.stopPropagation();
            }
        });

        this.element.addEventListener("show.bs.modal", () =>
            this.updateSelectionState()
        );
        this.element.addEventListener("hidden.bs.modal", () =>
            this.resetInputs()
        );
    }

    refreshFromInputs() {
        this.states.speakers = this.getCheckboxValue("speakersCheckbox");
        this.states.timestamps = this.getCheckboxValue("timestampsCheckbox");
        this.states.subtitles = this.getCheckboxValue("subtitlesCheckbox");
        this.states.maxLines = this.getSelectValue("selectStroke", 2);
        this.states.maxSymbols = this.getSelectValue("selectSymbol", 35);
    }

    resetInputs() {
        this.setCheckboxValue("speakersCheckbox", false);
        this.setCheckboxValue("timestampsCheckbox", false);
        this.setCheckboxValue("subtitlesCheckbox", false);
        this.states.speakers = false;
        this.states.timestamps = false;
        this.states.subtitles = false;
        this.toggleSpeakerSelect();
        this.updateHrefs();
    }

    updateSelectionState() {
        this.selectedIds = Object.keys(
            this.transcriptionsList?.selectedItems || {}
        );

        let hasWithSpeakers = false;
        let hasWithoutSpeakers = false;

        this.selectedIds.forEach((id) => {
            const transcription =
                this.transcriptionsList?.getTranscriptionById(id);
            const withSpeakers = this.readWithSpeakers(transcription);
            hasWithSpeakers = hasWithSpeakers || withSpeakers;
            hasWithoutSpeakers = hasWithoutSpeakers || !withSpeakers;
        });

        if (!hasWithSpeakers) {
            this.speakersOption?.hide();
            this.subtitlesOption?.hide();
            this.toggleHidden("mixedTooltip", true);
            this.subtitlesExportLink?.hide();
            this.setCheckboxValue("speakersCheckbox", false);
            this.setCheckboxValue("subtitlesCheckbox", false);
            this.states.speakers = false;
            this.states.subtitles = false;
            this.setSrtDisabled(false);
            this.toggleSpeakerSelect();
            this.updateHrefs();
            return;
        }

        this.speakersOption?.show();
        this.subtitlesOption?.show();
        this.subtitlesExportLink?.show();
        this.toggleHidden("mixedTooltip", !(hasWithSpeakers && hasWithoutSpeakers));
        this.setSrtDisabled(hasWithSpeakers && hasWithoutSpeakers);
        this.updateHrefs();
    }

    setSrtDisabled(disabled) {
        this.isSrtDisabled = disabled;
        const link = this.subtitlesExportLink?.element;
        if (!link) return;

        if (disabled) {
            link.classList.add("is-disabled");
            this.toggleHidden("srtDisabledTooltip", false);
        } else {
            link.classList.remove("is-disabled");
            this.toggleHidden("srtDisabledTooltip", true);
        }
    }

    updateHrefs() {
        this.refreshFromInputs();
        this.toggleSpeakerSelect();

        ["text", "word", "excel", "subtitles"].forEach((type) => {
            const link = this[`${type}ExportLink`];
            if (!link) {
                console.warn(`Export link for ${type} not found`);
                return;
            }

            try {
                const url = new URL(link.element.href);
                url.searchParams.set("type", type);
                url.searchParams.set(
                    "speakers",
                    this.states.speakers ? "1" : "0"
                );
                url.searchParams.set(
                    "timestamps",
                    this.states.timestamps ? "1" : "0"
                );
                url.searchParams.delete("ids");
                url.searchParams.delete("ids[]");
                this.selectedIds.forEach((id) =>
                    url.searchParams.append("ids[]", id)
                );

                if (this.states.subtitles) {
                    url.searchParams.set(
                        "maxLineCount",
                        this.states.maxLines.toString()
                    );
                    url.searchParams.set(
                        "maxLineWidth",
                        this.states.maxSymbols.toString()
                    );
                } else {
                    url.searchParams.delete("maxLineCount");
                    url.searchParams.delete("maxLineWidth");
                }

                link.element.href = url.toString();
            } catch (error) {
                console.error(`Failed to update ${type} export link:`, error);
            }
        });
    }

    toggleSpeakerSelect() {
        this.toggleHidden(this.selectSpeaker, !this.states.subtitles);
    }

    bindCheckbox(name, handler) {
        const component = this[name];
        if (component?.on) {
            component.on("change", () => handler(component.isChecked()));
            return;
        }
        const input = this.element.querySelector(`[data-tbx-name="${name}"]`);
        input?.addEventListener("change", (event) =>
            handler(event.target.checked)
        );
    }

    getCheckboxValue(name) {
        const input =
            this[name]?.element ||
            this.element.querySelector(`[data-tbx-name="${name}"]`);
        return input?.checked || false;
    }

    setCheckboxValue(name, value) {
        const input =
            this[name]?.element ||
            this.element.querySelector(`[data-tbx-name="${name}"]`);
        if (input) input.checked = value;
    }

    getSelectValue(name, fallback) {
        const input =
            this.selectSpeaker?.[name]?.element ||
            this.element.querySelector(`[data-tbx-name="${name}"]`);
        return input ? parseInt(input.value, 10) : fallback;
    }

    toggleHidden(component, shouldHide) {
        let element = component;
        if (typeof component === "string") {
            element = this.getNamedElement(component);
        } else if (component?.element) {
            element = component.element;
        }
        if (element) element.classList.toggle("d-none", shouldHide);
    }

    getNamedElement(name) {
        const target = this[name];
        if (target?.element) return target.element;
        if (target instanceof HTMLElement) return target;
        return this.element.querySelector(`[data-tbx-name="${name}"]`);
    }

    readWithSpeakers(transcription) {
        if (!transcription) return false;
        if (typeof transcription.withSpeakers !== "undefined") {
            return Boolean(transcription.withSpeakers);
        }
        const raw = transcription.element?.dataset?.tbxVarWithSpeakers;
        if (typeof raw === "string") {
            return raw === "1" || raw.toLowerCase() === "true";
        }
        return Boolean(raw);
    }
}
